// BASELINE landing page 

(() => {
  'use strict';

  // Only the first copy of this script on the page runs
  const self = document.currentScript;
  if (self && self !== document.querySelector(`script[src="${self.getAttribute('src')}"]`)) return;

  const $$ = (sel, root) => [...root.querySelectorAll(sel)];
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Color swatches: selecting a color shows Safariland's studio photo of that exact product.
     Purely a preview; size and color are chosen on the product page.
     Markup: [data-swatches] holding radios with data-src (and optional data-srcset),
     plus [data-swatch-img] and [data-swatch-name]; optional [data-alt="… {color}"]. */
  function initSwatches(root) {
    $$('[data-swatches]', root).forEach((group) => {
      const img = group.querySelector('[data-swatch-img]');
      const name = group.querySelector('[data-swatch-name]');
      const { alt } = group.dataset;
      const radios = $$('input[type="radio"][data-src]', group);

      // Warm the other photos once someone starts interacting, so switching is instant
      const warm = () => radios.forEach((r) => { new Image().src = r.dataset.src; });
      ['pointerenter', 'focusin'].forEach((type) => group.addEventListener(type, warm, { once: true }));

      group.addEventListener('change', (e) => {
        const radio = e.target;
        if (!radio.dataset.src) return;
        const color = radio.value;
        if (name) name.textContent = color;
        const swap = () => {
          if (radio.dataset.srcset) img.srcset = radio.dataset.srcset;
          img.src = radio.dataset.src;
          if (alt) img.alt = alt.replace('{color}', color);
        };
        if (!document.startViewTransition || reducedMotion()) return swap();
        // Crossfade the image's frame, not the image: the frame clips its contents,
        // so the fading snapshots can't spill past the allotted space (e.g. while hover-zoomed)
        const frame = img.closest('figure') ?? img;
        frame.style.viewTransitionName = 'bl-swatch-img';
        document.startViewTransition(swap).finished.finally(() => { frame.style.viewTransitionName = ''; });
      });
    });
  }

  /* Setup video chapters */
  function initChapters(root) {
    $$('[data-chapters]', root).forEach((list) => {
      const video = root.querySelector(`#${CSS.escape(list.dataset.chapters)}`);
      if (!video) return;
      const buttons = $$('button[data-t]', list);

      buttons.forEach((btn) => btn.addEventListener('click', () => {
        // Setting currentTime before the video has loaded makes it the start position,
        // so this works whether or not playback has begun (and with preload="none")
        video.currentTime = Number(btn.dataset.t);
        video.play().catch(() => {});
        video.focus({ preventScroll: true });
        video.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
      }));

      video.addEventListener('timeupdate', () => {
        let current = null;
        buttons.forEach((b) => { if (video.currentTime >= Number(b.dataset.t)) current = b; });
        buttons.forEach((b) => {
          if (b === current) b.setAttribute('aria-current', 'true');
          else b.removeAttribute('aria-current');
        });
      });
    });
  }

  /* Mobile buy bar (part of the hero section): appears after the hero, steps aside while a
     belts section is visible, and stays hidden from the closing CTA onward so it never covers
     the theme footer. Sections mark themselves with data-bl-buybar-hide / data-bl-buybar-end;
     only BASELINE sections are looked at. Uses `inert` so it is never focusable while hidden. */
  function initBuybar(root) {
    const bar = root.querySelector('[data-buybar]');
    if (!bar) return;
    const start = root.querySelector('[data-bl-buybar-start]');
    const hide = $$('[data-bl-root] [data-bl-buybar-hide]', document);
    const end = document.querySelector('[data-bl-root] [data-bl-buybar-end]');
    let pastStart = false;
    let pastEnd = false;
    const over = new Set();

    const sync = () => {
      const show = pastStart && !pastEnd && over.size === 0;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
    };
    sync();
    if (start) new IntersectionObserver(([e]) => { pastStart = !e.isIntersecting && e.boundingClientRect.top < 0; sync(); }).observe(start);
    if (hide.length) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? over.add(e.target) : over.delete(e.target)));
        sync();
      }, { threshold: 0.1 });
      hide.forEach((el) => io.observe(el));
    }
    if (end) new IntersectionObserver(([e]) => { pastEnd = e.isIntersecting || e.boundingClientRect.top < 0; sync(); }).observe(end);
  }

  /* Anatomy diagram: hovering or focusing a list item highlights its pin, and hovering a pin
     highlights its list item. On touch screens a tapped pin stays highlighted until the next tap. */
  function initAnatomy(root) {
    $$('[data-anatomy]', root).forEach((diagram) => {
      const setActive = (id) => {
        $$('[data-pin]', diagram).forEach((p) => p.classList.toggle('is-active', p.dataset.pin === id));
        $$('[data-part]', diagram).forEach((p) => p.classList.toggle('is-active', p.dataset.part === id));
      };
      $$('[data-part]', diagram).forEach((item) => {
        const id = item.dataset.part;
        item.addEventListener('pointerenter', () => setActive(id));
        item.addEventListener('focusin', () => setActive(id));
      });
      $$('[data-pin]', diagram).forEach((pin) => {
        const id = pin.dataset.pin;
        pin.addEventListener('pointerenter', () => setActive(id));
        pin.addEventListener('pointerleave', (e) => { if (e.pointerType !== 'touch') setActive(null); });
        pin.addEventListener('click', () => setActive(id));
      });
      diagram.addEventListener('pointerleave', (e) => { if (e.pointerType !== 'touch') setActive(null); });
      diagram.addEventListener('focusout', (e) => { if (!diagram.contains(e.relatedTarget)) setActive(null); });
    });
  }

  function init(root) {
    if (root.dataset.blReady) return;
    root.dataset.blReady = 'true';
    initSwatches(root);
    initChapters(root);
    initBuybar(root);
    initAnatomy(root);
  }

  document.querySelectorAll('[data-bl-root]').forEach(init);

  // Theme editor: re-run when the section is re-rendered after a settings change
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-bl-root]').forEach(init);
  });
})();
